import { useEffect, useState } from 'react';
import { Photo } from '../../Hooks/UsePhotos';
import dayjs from '../../Abstracts/Time';

interface ViewPhotoModalProps {
	photo: Photo;
	hash: string;
	onClose: () => void;
	onDownload?: () => void;
	onDelete?: () => void;
}

export default function ViewPhotoModal(props: ViewPhotoModalProps) {
	const { photo, hash, onClose, onDownload, onDelete } = props;
	const [isZoomed, setIsZoomed] = useState(false);
	const [touchStartY, setTouchStartY] = useState<number | null>(null);

	// Keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		document.body.style.overflow = 'hidden'; // Prevent background scroll

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = 'unset';
		};
	}, [onClose]);

	const handleBackdropClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	// Touch gestures for mobile
	const handleTouchStart = (e: React.TouchEvent) => {
		const touch = e.touches[0];
		if (touch) {
			setTouchStartY(touch.clientY);
		}
	};

	const handleTouchEnd = (e: React.TouchEvent) => {
		const touch = e.touches[0];
		if (touchStartY !== null && touch) {
			const touchEndY = touch.clientY;
			const deltaY = touchStartY - touchEndY;

			// Swipe down to close (minimum 100px swipe)
			if (deltaY < -100) {
				onClose();
			}
		}
		setTouchStartY(null);
	};

	const handleImageClick = () => {
		setIsZoomed(!isZoomed);
	};

	const formatFileSize = (bytes: number) => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
	};

	const formatDate = (timestamp: number) => {
		return dayjs.unix(timestamp).format('MMMM D, YYYY [at] h:mm A');
	};

	return (
		<div
			className="view-photo-modal-backdrop"
			onClick={handleBackdropClick}
			onTouchStart={handleTouchStart}
			onTouchEnd={handleTouchEnd}
		>
			<div className="view-photo-modal-container">
				<div className="view-photo-modal-header">
					<h3 className="view-photo-modal-title">Photo Details</h3>
					<div className="view-photo-modal-actions">
						{onDownload && (
							<button className="btn btn-default btn-sm" onClick={onDownload} title="Download">
								<i className="fas fa-download"></i>
							</button>
						)}
						{onDelete && (
							<button className="btn btn-danger btn-sm" onClick={onDelete} title="Delete">
								<i className="fas fa-trash"></i>
							</button>
						)}
						<button className="btn btn-default btn-sm" onClick={onClose} aria-label="Close photo viewer">
							<i className="fas fa-times"></i>
						</button>
					</div>
				</div>

				<div className="view-photo-modal-image-container">
					<img
						className={`view-photo-modal-image ${isZoomed ? 'zoomed' : ''}`}
						src={`/u/photos/image/${hash}/${photo.clientUploadFilename}`}
						alt={photo.clientUploadFilename}
						onClick={handleImageClick}
					/>
					{!isZoomed && (
						<div className="view-photo-modal-tap-hint">
							<span>Tap to zoom</span>
						</div>
					)}
				</div>

				<div className="view-photo-modal-info">
					<div className="view-photo-modal-info-item">
						<span className="view-photo-modal-info-label">File Size:</span>
						<span className="view-photo-modal-info-value">{formatFileSize(photo.clientUploadSize)}</span>
					</div>
					<div className="view-photo-modal-info-item">
						<span className="view-photo-modal-info-label">Uploaded:</span>
						<span className="view-photo-modal-info-value">{formatDate(photo.clientUploadTime)}</span>
					</div>
					<div className="view-photo-modal-info-item">
						<span className="view-photo-modal-info-label">Format:</span>
						<span className="view-photo-modal-info-value">{photo.clientUploadExtension.toUpperCase()}</span>
					</div>
				</div>

				<div className="view-photo-modal-mobile-hint">
					<p>Swipe down to close</p>
				</div>
			</div>
		</div>
	);
}
